import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ReleaseDetail } from '../models/release-detail';
import {
  ReleaseReportReleasesResponse,
  ReviewClassification,
} from '../models/release';

@Injectable({
  providedIn: 'root',
})
export class ReleaseReportService {
  private readonly apiBaseUrl = environment.releaseReportConfig.baseUrl;
  constructor(private readonly http: HttpClient) {}

  private getUrl(
    endpoint: string,
    queryParameters: { [key: string]: string | undefined } | undefined
  ): string {
    const url = new URL(`${this.apiBaseUrl}/${endpoint}`);
    if (queryParameters) {
      Object.keys(queryParameters).forEach((key) =>
        url.searchParams.append(key, queryParameters[key]?.toString() ?? '')
      );
    }

    return url.toString();
  }

  private getPostUrl(endpoint: string): string {
    let url = `${this.apiBaseUrl}/${endpoint}`;
    return url;
  }

  private get<T>(
    endpoint: string,
    queryParameters: { [key: string]: string | undefined } | undefined
  ): Observable<T> {
    const url = this.getUrl(endpoint, queryParameters);
    return this.http.get<T>(url, { observe: 'body' });
  }

  private post<T>(endpoint: string, body: any): Observable<T> {
    const url = this.getPostUrl(endpoint);
    return this.http.post<T>(url, body, { observe: 'body' });
  }

  addDetails(body: any): Observable<Object> {
    return this.post<Object[]>('releasedetails', body);
  }

  updateRelease(body: any): Observable<string> {
    // Set legacy fields based on ReviewClassification
    body.Reviewed =
      body.ReviewClassification !== ReviewClassification.NeedsReview;
    body.IsTransient =
      body.ReviewClassification !== ReviewClassification.NeedsReview &&
      body.ReviewClassification !== ReviewClassification.Reviewed;

    return this.http.put(
      `${this.apiBaseUrl}/release?releaseId=${body.ReleaseId}`,
      body,
      { observe: 'body', responseType: 'text' }
    );
  }

  updateDescription(detail: ReleaseDetail): Observable<Object> {
    return this.http.put<Object>(
      `${this.apiBaseUrl}/releasedetails?descriptionId=${detail.DescriptionId}`,
      detail,
      { observe: 'body' }
    );
  }

  getReleases(
    startDateUtc?: string,
    endDateUtc?: string
  ): Observable<ReleaseReportReleasesResponse[]> {
    const queryParams = {
      startDateUtc: startDateUtc,
      endDateUtc: endDateUtc,
    };

    return this.get<ReleaseReportReleasesResponse[]>('releases', queryParams);
  }

  getReleaseDates(): Observable<string[]> {
    return this.get<string[]>('releasedates', undefined);
  }

  getReleaseReport(
    startDateUtc?: string,
    endDateUtc?: string
  ): Observable<Object[]> {
    const queryParams = {
      startDateUtc: startDateUtc,
      endDateUtc: endDateUtc,
    };

    return this.get<Object[]>('releasereports', queryParams);
  }

  getReleaseNotes(
    startDateUtc?: string,
    endDateUtc?: string
  ): Observable<Object> {
    const queryParams = {
      startDateUtc: startDateUtc,
      endDateUtc: endDateUtc,
    };

    return this.get<Object>('releasenote', queryParams);
  }

  getExecutiveSummary(
    startDateUtc?: string,
    endDateUtc?: string
  ): Observable<Object> {
    const queryParams = {
      startDateUtc: startDateUtc,
      endDateUtc: endDateUtc,
    };

    return this.get<Object>('execsummary', queryParams);
  }
}
